import { Link } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { useCartStore } from '@/stores/cart';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

export function CartPage() {
  const { items, updateQuantity, removeItem, getSubtotal, getTotal, discountAmount, promoCode } = useCartStore();

  const subtotal = getSubtotal();
  const total = getTotal();
  const tax = (subtotal - discountAmount) * 0.0825;

  if (items.length === 0) {
    return (
      <div className="container-narrow py-16">
        <EmptyState
          icon={
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          }
          title="Your cart is empty"
          description="Browse our pro shop to find great gear."
          action={
            <Link to="/shop">
              <Button variant="sage">Browse Shop</Button>
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="bg-sand-50 min-h-screen py-8">
      <div className="container-wide">
        <h1 className="text-3xl font-bold text-sand-900 mb-8">Shopping Cart</h1>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <Card key={`${item.productId}-${item.variantId}`}>
                <div className="flex gap-4">
                  <div className="w-24 h-24 flex-shrink-0 rounded-lg bg-sand-100 overflow-hidden">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <svg className="h-8 w-8 text-sand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-medium text-sand-900">{item.name}</h3>
                      <button
                        onClick={() => removeItem(item.productId, item.variantId)}
                        className="text-sand-400 hover:text-sand-600"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <p className="text-sage-600 font-medium mt-1">{formatCurrency(item.price)}</p>

                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center border border-sand-200 rounded-lg">
                        <button
                          onClick={() => updateQuantity(item.productId, item.quantity - 1, item.variantId)}
                          className="px-2 py-1 text-sand-600 hover:text-sand-900"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="px-3 py-1 text-sm text-sand-900">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.productId, item.quantity + 1, item.variantId)}
                          className="px-2 py-1 text-sand-600 hover:text-sand-900"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>

                      <p className="text-sand-700 font-medium">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <h2 className="text-lg font-semibold text-sand-900 mb-4">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sand-700">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sage-600">
                    <span>Discount ({promoCode})</span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sand-700">
                  <span>Tax</span>
                  <span>{formatCurrency(tax)}</span>
                </div>
                <hr className="border-sand-200" />
                <div className="flex justify-between text-lg font-semibold text-sand-900">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>

              <Link to="/checkout">
                <Button variant="sage" className="w-full">
                  Proceed to Checkout
                </Button>
              </Link>

              <Link to="/shop" className="block mt-4">
                <Button variant="ghost" className="w-full">
                  Continue Shopping
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
